"use client";

import { useState } from "react";
import { Header, Footer } from "./AdditionalPages";
import { go, goWithLogin } from "./AdditionalPages";
import MotionScope from "./MotionScope";
import { ParkDirectory, PolicyDirectory } from "./directories";
import { ActivityHome, OpcHome, InvestHome } from "./homeBlocks";
import ProductMarket from "./ProductMarket";
import type { ParkItem, ProvinceCount } from "@/common/interface/park";
import type { OpcItem, OpcProduct } from "@/common/interface/opc";
import type { PolicyItem } from "@/common/interface/policy";
import type { ActivityItem } from "@/common/interface/activity";
import type { InvestOpportunity } from "@/common/interface/invest";
import styles from "./home.module.scss";

const svgProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

/** 首页：hero + 园区/服务/产品/活动/政策/OPC 推荐 + 云能力。数据 SSR 传入。 */
export default function HomePage({
  parks,
  provinces,
  opcs,
  products,
  policies,
  activities,
  invests,
}: {
  parks: ParkItem[];
  provinces: ProvinceCount[];
  opcs: OpcItem[];
  products: OpcProduct[];
  policies: PolicyItem[];
  activities: ActivityItem[];
  invests: InvestOpportunity[];
}) {
  const [tab, setTab] = useState<"park" | "policy">("park");
  return (
    <MotionScope className={styles.page} route="home">
      <Header active="" />
      <main>
        <section className="hero new-hero">
          <div className="hero-copy">
            <h1>
              <i className="hl">一个人</i>创业，<br />也能连接<i className="hl">一座城</i>的资源
            </h1>
            <p>找园区、找政策、找服务及产品、找销路、找投资、找活动。百度智能云联合城市园区、企业和生态机构，为一人企业提供可申请、可对接、可跟踪的经营资源。</p>
            <div className="hero-roles">
              <button onClick={() => goWithLogin("/join-opc")}>
                <span>
                  <b>我是 OPC</b>
                  <small>连接园区、客户，入驻云市场获海量商机</small>
                </span>
                <em>申请入驻 →</em>
              </button>
              <button onClick={() => goWithLogin("/join-park")}>
                <span>
                  <b>我是园区 / 投资机构</b>
                  <small>发布政策、投资方向与合作资源</small>
                </span>
                <em>机构入驻 →</em>
              </button>
            </div>
          </div>
          <div className="benefit-card">
            <span>OPC 专属活动</span>
            <h2>
              完成企业认证<br />领取<i className="hl">¥9200</i>专享优惠券包
            </h2>
            <p>模型体验额度、云产品优惠券、技术咨询与园区服务，可在认证后按活动规则申请。</p>
            <button onClick={() => go("https://cloud.baidu.com/campaign/event/enterprise/index.html?track=opc_commutivity")}>查看可申请权益 →</button>
            <small>权益内容以实际活动规则和审核结果为准</small>
          </div>
        </section>
        <section className="soft park-home">
          <div className="section-title">
            <div>
              <h2>{tab === "park" ? "找园区" : "找政策"}</h2>
              <p>
                {tab === "park"
                  ? "按地区查看认证园区、产业方向，以及可入驻的 OPC 合作资源。"
                  : "按地区查看 OPC 与人工智能创业扶持政策，均可直达发布机构官网原文。"}
              </p>
            </div>
            <button onClick={() => go(tab === "park" ? "/cities" : "/policies")}>
              {tab === "park" ? "浏览全国园区 →" : "查看全部政策 →"}
            </button>
          </div>
          <div className="directory-major-tabs">
            <button className={tab === "park" ? "active" : ""} onClick={() => setTab("park")}>
              合作园区
            </button>
            <button className={tab === "policy" ? "active" : ""} onClick={() => setTab("policy")}>
              扶持政策
            </button>
          </div>
          <div style={{ display: tab === "park" ? undefined : "none" }}>
            <ParkDirectory parks={parks} provinces={provinces} capped />
          </div>
          <div style={{ display: tab === "policy" ? undefined : "none" }}>
            <PolicyDirectory policies={policies} capped />
          </div>
        </section>
        <div className="section-title">
          <div>
            <h2>找服务及产品</h2>
            <p>接入百度智能云企业服务中心，为 OPC 提供企业基础服务与按需选购的单品资源。</p>
          </div>
          <button onClick={() => go("/growth")}>进入企业服务中心 →</button>
        </div>
        <ProductMarket />
        <div className="section-title">
          <div>
            <h2>找销路</h2>
            <p>入驻百度智能云云市场，让成熟的 OPC 产品进入企业采购场景，获得展示、交易与生态合作机会。</p>
          </div>
          <button onClick={() => go("/sales")}>进入找销路 →</button>
        </div>
        <section className="market-home">
          <div className="market-home-copy">
            <span>百度智能云云市场</span>
            <h2>入驻云市场 获海量商机</h2>
            <p>云市场面向具备企业主体、标准产品、明确价格与稳定交付能力的 OPC 开放。完成服务商和商品审核后，可获得商品展示、在线交易、客户触达及百度智能云生态合作机会。</p>
            <div className="market-home-checks">
              <b>企业主体及相关资质完整</b>
              <b>产品或服务已完成标准化</b>
              <b>价格、交付范围与服务承诺清晰</b>
              <b>具备可验证案例与售后能力</b>
            </div>
            <div className="market-home-actions">
              <button onClick={() => go("https://market.baidu.com/")}>了解云市场并申请入驻 →</button>
            </div>
          </div>
          <div className="market-adv">
            <div className="market-adv-item">
              <i>
                <svg {...svgProps}>
                  <path d="M4 16l5-5 3 3 6-7" />
                  <path d="M14 7h4v4" />
                </svg>
              </i>
              <b>海量商机</b>
              <small>触达百度智能云企业采购需求</small>
            </div>
            <div className="market-adv-item">
              <i>
                <svg {...svgProps}>
                  <path d="M3 4h2l2 11h10l2-8H6" />
                  <circle cx="9" cy="19" r="1.4" />
                  <circle cx="17" cy="19" r="1.4" />
                </svg>
              </i>
              <b>在线交易</b>
              <small>标准化商品展示与在线成交</small>
            </div>
            <div className="market-adv-item">
              <i>
                <svg {...svgProps}>
                  <circle cx="9" cy="8" r="3" />
                  <path d="M3 20a6 6 0 0 1 12 0" />
                  <path d="M16 6a3 3 0 0 1 0 6" />
                  <path d="M18.5 20a5 5 0 0 0-3-4.6" />
                </svg>
              </i>
              <b>客户触达</b>
              <small>精准连接目标企业客户</small>
            </div>
            <div className="market-adv-item">
              <i>
                <svg {...svgProps}>
                  <circle cx="12" cy="12" r="9" />
                  <path d="M3 12h18" />
                  <path d="M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18" />
                </svg>
              </i>
              <b>海外通路</b>
              <small>拓展跨境与生态合作机会</small>
            </div>
          </div>
        </section>
        <div className="section-title">
          <div>
            <h2>找 OPC</h2>
            <p>来自公开开发者社区的真实主体与产品，均可通过来源链接核验。</p>
          </div>
          <button onClick={() => go("/opcs")}>查看全部 OPC →</button>
        </div>
        <OpcHome opcs={opcs} products={products} limit={3} />
        <div className="section-title">
          <div>
            <h2>找投资</h2>
            <p>汇集政府部门、创业赛事、产业园区及公共服务平台公开发布的项目征集与融资活动，均可直达官方渠道自主报名。</p>
          </div>
          <button onClick={() => go("/investment")}>查看全部投资机会 →</button>
        </div>
        <section className="invest-home-section">
          <InvestHome list={invests} />
        </section>
        <section className="event-section">
          <div className="section-title">
            <div>
              <h2>找活动</h2>
              <p>参加产业活动，提交项目，获得评估、展示与推荐机会。</p>
            </div>
            <button onClick={() => go("/venture")}>查看全部活动 →</button>
          </div>
          <ActivityHome activities={activities} listCount={3} />
        </section>
      </main>
      <Footer />
    </MotionScope>
  );
}
